import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Textarea } from "../../../design/textarea";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { useAttorney } from "../../../contexts/AttorneyContext";
import PhoneNumberInput from "../../../components/PhoneNumberInput";
import { recordWebTelemetry } from "../../../utils/telemetry";

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  firmName?: string;
  submit?: string;
}

const CreateAttorneyButton: React.FC = () => {
  const { createAttorney } = useAttorney();
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [suffix, setSuffix] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [mobilePhone, setMobilePhone] = useState<string>("");
  const [website, setWebsite] = useState<string>("");
  const [firmName, setFirmName] = useState<string>("");
  const [barNumber, setBarNumber] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  // Legal Aid fields
  const [legalAidName, setLegalAidName] = useState<string>("");
  const [legalAidEmail, setLegalAidEmail] = useState<string>("");
  const [legalAidPhone, setLegalAidPhone] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const cleanPhoneNumber = phoneNumber
        ? `+${phoneNumber.replace(/\D/g, "")}`
        : undefined;
      const cleanMobilePhone = mobilePhone
        ? `+${mobilePhone.replace(/\D/g, "")}`
        : undefined;
      const cleanLegalAidPhone = legalAidPhone
        ? `+${legalAidPhone.replace(/\D/g, "")}`
        : undefined;
      await createAttorney({
        firstName,
        lastName,
        suffix: suffix || undefined,
        phoneNumber: cleanPhoneNumber,
        mobilePhone: cleanMobilePhone,
        website: website || undefined,
        email: email || undefined,
        firmName: firmName || undefined,
        barNumber: barNumber || undefined,
        notes: notes || undefined,
        legalAidName: legalAidName || undefined,
        legalAidEmail: legalAidEmail || undefined,
        legalAidPhone: cleanLegalAidPhone,
        status: "active",
      });
      clearForm();
    } catch (error: any) {
      // Previously this only logged to the console, so a failed save looked
      // like nothing happened ("I can't add an attorney"). Surface it.
      console.error("Error creating attorney:", error);
      const code = error?.code || "";
      const msg =
        code === "permission-denied"
          ? "You don't have permission to add an attorney. Please contact support."
          : error?.message || "Couldn't add the attorney. Please try again.";
      setErrors((prev) => ({ ...prev, submit: msg }));
      recordWebTelemetry("error", "create_attorney_failed", {
        code: code || "unknown",
        message: error?.message?.slice(0, 200),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validate = () => {
    const newErrors: Errors = {};
    if (!firstName) newErrors.firstName = "First Name is required";
    if (!lastName) newErrors.lastName = "Last Name is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email address";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = () => {
    setIsOpen(false);
    setFirstName("");
    setLastName("");
    setSuffix("");
    setEmail("");
    setPhoneNumber("");
    setMobilePhone("");
    setWebsite("");
    setFirmName("");
    setBarNumber("");
    setNotes("");
    setLegalAidName("");
    setLegalAidEmail("");
    setLegalAidPhone("");
    setErrors({});
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Add Attorney
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Add Attorney</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-4">
                <Field className="sm:col-span-1">
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field className="sm:col-span-1">
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
                <Field className="sm:col-span-1">
                  <Label>Suffix</Label>
                  <Input
                    type="text"
                    value={suffix}
                    placeholder="Esq."
                    onChange={(e) => setSuffix(e.target.value)}
                  />
                </Field>
              </div>
              <Field>
                <Label>Firm Name</Label>
                <Input
                  type="text"
                  value={firmName}
                  placeholder="Law Firm Name"
                  onChange={(e) => setFirmName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  placeholder="attorney@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Office Phone</Label>
                  <PhoneNumberInput
                    defaultValue={phoneNumber}
                    value={phoneNumber}
                    placeholder="Office Phone"
                    onChange={setPhoneNumber}
                  />
                </Field>
                <Field>
                  <Label>Mobile Phone</Label>
                  <PhoneNumberInput
                    defaultValue={mobilePhone}
                    value={mobilePhone}
                    placeholder="Mobile Phone"
                    onChange={setMobilePhone}
                  />
                </Field>
              </div>
              <Field>
                <Label>Website</Label>
                <Input
                  type="url"
                  value={website}
                  placeholder="https://lawfirm.com"
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Bar Number</Label>
                <Input
                  type="text"
                  value={barNumber}
                  placeholder="State Bar Number"
                  onChange={(e) => setBarNumber(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  placeholder="Additional notes about this attorney..."
                  rows={3}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>

              {/* Legal Aid / Assistant Section */}
              <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700">
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-4">
                  Legal Aid / Assistant
                </p>
                <div className="space-y-4">
                  <Field>
                    <Label>Name</Label>
                    <Input
                      type="text"
                      value={legalAidName}
                      placeholder="Legal Aid Name"
                      onChange={(e) => setLegalAidName(e.target.value)}
                    />
                  </Field>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                    <Field>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={legalAidEmail}
                        placeholder="legalaid@example.com"
                        onChange={(e) => setLegalAidEmail(e.target.value)}
                      />
                    </Field>
                    <Field>
                      <Label>Phone</Label>
                      <PhoneNumberInput
                        defaultValue={legalAidPhone}
                        value={legalAidPhone}
                        placeholder="Phone Number"
                        onChange={setLegalAidPhone}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        {errors.submit && (
          <div className="px-6">
            <ErrorMessage>{errors.submit}</ErrorMessage>
          </div>
        )}
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateAttorneyButton;
